import {Async, Empty} from 'Core/models/Async';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ValidPassword} from 'Core/models/Password';
import {Lens} from 'monocle-ts';
import {compose} from 'redux';
import {createAction} from 'Shared/utilities/actionCreator';
import {IndependentAgentProfile} from 'Users/models/IndependentAgentProfile';
import {
  EmptyPasswordResetForm,
  PasswordResetForm,
  PasswordUpdate,
  validatePasswordResetForm,
  ValidPasswordResetForm,
} from 'Users/models/PasswordResetForm';
import {SecurityQuestionByPasswordPayload} from 'Users/models/SecurityQuestionByPasswordPayload';

export const InvalidUsernameMessage = 'Incorrect Username. Please try again';
export const ErrorUsernameMessage =
  'We have encountered an error in your profile. Support has been notified and a member of our team will be in contact with you shortly.';

export enum PasswordRecoveryActionType {
  REQUEST_PASSWORD_RESET = 'passwordRecovery/REQUEST_PASSWORD_RESET',
  REQUEST_PASSWORD_RESET_QUES = 'passwordRecovery/REQUEST_PASSWORD_RESET_QUES',
  CHANGE_USERNAME = 'passwordRecovery/CHANGE_USERNAME',
  VALID_USERNAME = 'passwordRecovery/VALID_USERNAME',
  VALID_ANSWERS = 'passwordRecovery/VALID_ANSWERS',
  CHANGE_PASSWORD_RESET_FORM = 'passwordRecovery/CHANGE_PASSWORD_RESET_FORM',
  GET_PASSWORD_RESET_REQUEST_QUESTIONS = 'passwordRecovery/GET_PASSWORD_RESET_REQUEST_QUESTIONS',
  CHANGE_PASSWORD_RESET_REQUEST_QUESTIONS = 'passwordRecovery/CHANGE_PASSWORD_RESET_REQUEST_QUESTIONS',
  UPDATE_PASSWORD = 'passwordRecovery/UPDATE_PASSWORD',
  CHANGE_SECURITY_QUESTIONS_PASSWORD_RECOVERY = 'passwordRecovery/CHANGE_SECURITY_QUESTIONS_PASSWORD_RECOVERY',
}

const getNewUpdateFromEntry = compose(form => PasswordUpdate.Entry({form}), validatePasswordResetForm);
const passwordResetFormLens = Lens.fromProp<PasswordResetForm>();
export const getPasswordResetRequestQuestions = (requestKey: string) =>
  createAction(PasswordRecoveryActionType.GET_PASSWORD_RESET_REQUEST_QUESTIONS, {requestKey});
export const changeSecurityQuestionsForPasswordRecovery = (
  questionsForPasswordRecovery: Async<SecurityQuestionByPasswordPayload[]>
) => createAction(PasswordRecoveryActionType.CHANGE_SECURITY_QUESTIONS_PASSWORD_RECOVERY, questionsForPasswordRecovery);
export const changePasswordResetRequestQuestions = (questions: Async<IndependentAgentProfile>) =>
  createAction(PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_REQUEST_QUESTIONS, questions);
export const changePasswordUpdate = (update: PasswordUpdate) =>
  createAction(PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_FORM, {update});
export const changeAnswerOne = (answerOne: string, existingForm: PasswordResetForm) =>
  createAction(PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_FORM, {
    update: getNewUpdateFromEntry(
      passwordResetFormLens('answerOne').set(NonEmptyString.create(answerOne))(existingForm)
    ),
  });
export const changeAnswerTwo = (answerTwo: string, existingForm: PasswordResetForm) =>
  createAction(PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_FORM, {
    update: getNewUpdateFromEntry(
      passwordResetFormLens('answerTwo').set(NonEmptyString.create(answerTwo))(existingForm)
    ),
  });
export const changePassword = (passwordLine1: string, passwordLine2: string, existingForm: PasswordResetForm) =>
  createAction(PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_FORM, {
    update: getNewUpdateFromEntry(
      passwordResetFormLens('password').set(ValidPassword.create(passwordLine1, passwordLine2))(existingForm)
    ),
  });
export const requestQuesForPasswordReset = (username: NonEmptyString) =>
  createAction(PasswordRecoveryActionType.REQUEST_PASSWORD_RESET_QUES, {username});
export const requestPasswordReset = (obj: NonEmptyString, username: StringUnion) =>
  createAction(PasswordRecoveryActionType.REQUEST_PASSWORD_RESET, {obj, username});
export const changeUsername = (username: StringUnion) =>
  createAction(PasswordRecoveryActionType.CHANGE_USERNAME, {username});
export const validUsername = (errorMessage: StringUnion) =>
  createAction(PasswordRecoveryActionType.VALID_USERNAME, {errorMessage});
export const validAns = (validAnswer: number) => createAction(PasswordRecoveryActionType.VALID_ANSWERS, {validAnswer});

export const updatePassword = (
  id: string,
  requestKey: string,
  independentAgentProfile: IndependentAgentProfile,
  passwordResetForm: ValidPasswordResetForm
) =>
  createAction(PasswordRecoveryActionType.UPDATE_PASSWORD, {
    id,
    requestKey,
    independentAgentProfile,
    passwordResetForm,
  });

export type PasswordRecoveryState = {
  username: StringUnion;
  passwordResetRequest: Async<IndependentAgentProfile>;
  passwordUpdate: PasswordUpdate;
  questionsForPasswordRecovery: Async<SecurityQuestionByPasswordPayload[]>;
  obj: Async<SecurityQuestionByPasswordPayload[]>;
  valid: boolean;
  validAns: number;
  errorMessage: StringUnion;
};

type PasswordRecoveryAction =
  | ReturnType<typeof changeUsername>
  | ReturnType<typeof validUsername>
  | ReturnType<typeof validAns>
  | ReturnType<typeof changePasswordResetRequestQuestions>
  | ReturnType<typeof changeAnswerOne>
  | ReturnType<typeof changeAnswerTwo>
  | ReturnType<typeof changeSecurityQuestionsForPasswordRecovery>
  | ReturnType<typeof changePassword>;

export const DEFAULT_STATE: PasswordRecoveryState = {
  username: new EmptyString(),
  passwordUpdate: PasswordUpdate.Entry({form: EmptyPasswordResetForm}),
  passwordResetRequest: new Empty(),
  questionsForPasswordRecovery: new Empty(),
  obj: new Empty(),
  valid: true,
  validAns: 3,
  errorMessage: new EmptyString(),
};

const PasswordRecoveryLens = Lens.fromProp<PasswordRecoveryState>();
export default (
  state: PasswordRecoveryState = DEFAULT_STATE,
  action: PasswordRecoveryAction
): PasswordRecoveryState => {
  switch (action.type) {
    case PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_REQUEST_QUESTIONS:
      return PasswordRecoveryLens('passwordResetRequest').set(action.payload)(state);
    case PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_FORM:
      return PasswordRecoveryLens('passwordUpdate').set(action.payload.update)(state);
    case PasswordRecoveryActionType.CHANGE_USERNAME:
      return PasswordRecoveryLens('username').set(action.payload.username)(state);
    case PasswordRecoveryActionType.VALID_USERNAME:
      return PasswordRecoveryLens('errorMessage').set(action.payload.errorMessage)(state);
    case PasswordRecoveryActionType.VALID_ANSWERS:
      return PasswordRecoveryLens('validAns').set(action.payload.validAnswer)(state);
    case PasswordRecoveryActionType.CHANGE_SECURITY_QUESTIONS_PASSWORD_RECOVERY:
      return PasswordRecoveryLens('questionsForPasswordRecovery').set(action.payload)(state);
    default:
      return state;
  }
};
